import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertCircle, Calendar } from 'lucide-react';

const TradePolicy = () => (
  <div className="pt-20">
    {/* Hero */}
    <section className="py-20 bg-gradient-to-br from-amber-500 to-amber-600 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <Link to="/subsidies" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} />
          Back to All Schemes
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-3">
            Rajasthan Trade Promotion Policy 2025
          </h1>
          <div className="flex items-center gap-2 mt-4 text-white/70">
            <Calendar size={16} />
            <span>Valid till: 31st March 2029</span>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Introduction */}
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-semibold text-[#0f172a] mb-4">Overview</h2>
            <p className="text-[#64748b] leading-relaxed text-lg">
              Rajasthan Trade Promotion Policy 2025 is a state-level policy aimed at strengthening the retail and wholesale trade sector by improving access to credit, promoting digital adoption, and enhancing ease of doing business. The policy is valid till 31st March 2029.
            </p>
          </div>
          <div>
            <div className="bg-[#f8fafc] rounded-xl p-6 border border-slate-100">
              <p className="font-medium text-[#0f172a] mb-1">Policy Period</p>
              <p className="text-[#64748b] text-sm">Valid till 31st March 2029</p>
              <div className="mt-4">
                <Link
                  to="/contact"
                  className="w-full gradient-btn px-6 py-3 rounded-full font-medium text-white inline-flex items-center justify-center gap-2"
                >
                  Get Expert Help
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Quantum & Nature of Financial Assistance */}
    <section className="py-16 bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-heading text-2xl font-semibold text-[#0f172a] mb-8"
        >
          Quantum &amp; Nature of Financial Assistance
        </motion.h2>
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-xl overflow-hidden shadow-sm">
            <thead className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left font-medium">Loan Amount</th>
                <th className="px-6 py-4 text-left font-medium">Interest Subsidy</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="px-6 py-4 font-medium text-[#0f172a]">Up to ₹1 Cr</td>
                <td className="px-6 py-4 text-[#64748b]">6% interest subsidy</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-6 py-4 font-medium text-[#0f172a]">₹1 Cr – ₹2 Cr</td>
                <td className="px-6 py-4 text-[#64748b]">4% interest subsidy</td>
              </tr>
              <tr>
                <td className="px-6 py-4 font-medium text-[#0f172a]">Additional 1% subsidy for</td>
                <td className="px-6 py-4 text-[#64748b]">SC/ST/Women/PwBD</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* Eligibility Conditions */}
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-heading text-2xl font-semibold text-[#0f172a] mb-8 flex items-center gap-3"
        >
          <CheckCircle2 className="text-green-500 w-7 h-7" />
          Eligibility Conditions
        </motion.h2>
        <ul className="space-y-4">
          {[
            'Applicable for New Micro Trading Enterprises',
            'Applicable for Proprietorship, Partnerships, LLP and Private Limited Company etc.',
            'For purchase of land, plant & machinery, construction of building, purchase of furniture, purchase of raw material etc.',
            'In case of land & building, only 25% of the loan amount is eligible for subsidy',
            'Up to 80% of the total loan amount can be used as working capital, which may be provided as a Working Capital Term Loan or a Cash Credit facility'
          ].map((item, index) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="flex items-start gap-3"
            >
              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-[#0f172a]">{item}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>

    {/* Special Features */}
    <section className="py-16 bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-heading text-2xl font-semibold text-[#0f172a] mb-8"
        >
          Special Features
        </motion.h2>
        <ul className="space-y-4">
          {[
            'The maximum loan amount eligible is ₹2 crore',
            'Loan Tenure can be maximum 7 years',
            'Interest subsidy is available for a period of 5 years inclusive of moratorium period of 6 months'
          ].map((item, index) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="flex items-start gap-3"
            >
              <span className="w-2 h-2 rounded-full bg-[#00bcd4] mt-2 flex-shrink-0"></span>
              <span className="text-[#0f172a]">{item}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>

    {/* Negative List */}
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-heading text-2xl font-semibold text-[#0f172a] mb-8 flex items-center gap-3"
        >
          <AlertCircle className="text-red-500 w-7 h-7" />
          Negative List
        </motion.h2>
        <ul className="space-y-4">
          {[
            'Automobile Dealers and Wholesalers (including 2-Wheeler, 4-Wheeler and other automobiles)',
            'Fuel Trading (including petroleum products distribution i.e. Petrol Pumps)',
            'Liquor Trading',
            'Trading of Polythene carry bags of less than 20 microns thickness',
            'Sectors/activities prohibited by State/Central Government from time to time'
          ].map((item, index) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-[#64748b]">{item}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-gradient-to-r from-amber-500 to-amber-600">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <h2 className="font-heading text-3xl font-semibold text-white mb-4">
          Ready to Apply for Rajasthan Trade Promotion Policy 2025?
        </h2>
        <p className="text-white/80 max-w-2xl mx-auto mb-8">
          Our experts will help you understand eligibility, prepare documentation, and guide you through the entire application process.
        </p>
        <Link
          to="/contact"
          className="px-10 py-4 bg-white text-[#0f172a] rounded-full font-medium hover:bg-white/90 transition-colors inline-flex items-center gap-2"
        >
          Get Started Today
        </Link>
      </div>
    </section>
  </div>
);

export default TradePolicy;
